// Standalone terms of service page. Same mounting pattern as the privacy policy:
// outside app shell, publicly crawlable, no session required.

const LAST_UPDATED = "July 8, 2026";
const CONTACT_EMAIL = "support@parable.so";
const GUEST_NOTICE_URL = "https://docs.parable.so/guests";

export function TermsOfService() {
  return (
    <div className="min-h-screen bg-wiki-bg px-6 py-16 text-wiki-fg-1">
      <div className="anim-fade-in mx-auto max-w-2xl space-y-8">
        <header className="space-y-2">
          <div className="label-caps text-wiki-fg-3">Parable</div>
          <h1 className="font-display text-4xl text-wiki-fg-1 italic">
            Terms of Service
          </h1>
          <p className="num-mono text-sm text-wiki-fg-3">
            Last updated {LAST_UPDATED}
          </p>
        </header>

        <Section title="1. Acceptance">
          <p>
            By installing or using Parable ("the Service"), you agree to these
            Terms of Service. If you don't agree, don't use the Service.
          </p>
        </Section>

        <Section title="2. What Parable does">
          <p>
            Parable is a company-memory assistant: an ambient capture device and
            app that transcribes your work conversations and stores searchable,
            speaker-attributed transcripts in your private workspace.
          </p>
        </Section>

        <Section title="3. Your account">
          <p>
            You're responsible for your credentials and for activity in your
            account. You must be at least 13 years old to use Parable, and you
            must comply with applicable law.
          </p>
        </Section>

        <Section title="4. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-outside list-disc space-y-2 pl-6 marker:text-wiki-fg-3">
            <li>Use Parable to harass, defame, or harm others.</li>
            <li>
              Abuse our APIs, reverse-engineer the Service, or break auth flows.
            </li>
            <li>Upload content you don't have rights to.</li>
          </ul>
          <p>We may suspend accounts that violate these rules.</p>
        </Section>

        <Section title="5. Recording consent">
          <p>
            Parable records and transcribes conversations. You and your
            workspace are responsible for using it lawfully in your jurisdiction
            — including providing notice to, and obtaining any required consent
            from, the people who are recorded. Many places, including several US
            states, require all parties' consent. Our{" "}
            <a
              href={GUEST_NOTICE_URL}
              className="text-wiki-accent hover:underline"
            >
              guest notice template
            </a>{" "}
            can help you meet these obligations for visitors.
          </p>
        </Section>

        <Section title="6. Your content">
          <p>
            Recordings, transcripts, and notes you create remain yours and are
            not claimed by us. We process them only as described in our Privacy
            Policy.
          </p>
        </Section>

        <Section title="7. Service availability">
          <p>
            Parable is provided "as is." We do our best to keep it reliable but
            don't guarantee uptime or error-free operation.
          </p>
        </Section>

        <Section title="8. Termination">
          <p>
            You can stop using Parable at any time and request deletion by
            contacting{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-wiki-accent hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            . We can suspend or terminate accounts that violate these terms.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Parable's total liability to
            you for any claim related to the Service is limited to the amount
            you paid us in the twelve months preceding the claim, or US$50,
            whichever is greater. We aren't liable for indirect, incidental, or
            consequential damages.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Questions about these terms?{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-wiki-accent hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl text-wiki-fg-1 italic">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-wiki-fg-2">
        {children}
      </div>
    </section>
  );
}
